import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { callNativeWeb, footerShowEventWeb, hideProgressWeb, showProgressWeb } from 'common/appInterface';
import { useLocation, useNavigate } from 'react-router';
import { Location } from 'history';
import client from 'lib/CommonApi/client';
import { isNullOrBlank } from 'common/utils';
import stringUtils from 'common/stringUtils';

const ApplyRequirements = () => {
  const screenId: string = "APPL24";

  const navigate = useNavigate();
  const location = useLocation() as Location<LocationState>;

  const {t, i18n} = useTranslation();

  // 내외국인 구분 코드(L: 내국인, F: 외국인)
  const [foreignerDivision, setForeignerDivision] = useState('');
  // 신분증 업로드 필요 여부(true: 화면 하단 신분증 요구사항 표시, false: 신분증 요구사항 미표기)
  const [isNeedIdUpload, setIsNeedIdUpload] = useState(false);

  // KYC 승인날짜 확인을 통해 신분증 업로드가 필요한지 확인
  const checkApprovalDate = useCallback(() => {
    showProgressWeb();

    client.get("/api/app/apply/info/approval/date", { params: location.state.query }).then((response) => {
      if (response.data.code === "00") {
        let betweenDays = 1000;
        let approvalDate = response.data.response.kycApprovalDate;

        if (!isNullOrBlank(approvalDate)) {
          betweenDays = stringUtils.betweenDays(response.data.response.kycApprovalDate);
        }

        if (betweenDays > 180) {
          // 기본값 false, 업로드 날짜가 6개월 이후면 신분증 업로드 필요
          setIsNeedIdUpload(true);
        }
      }
    }).finally(() => {
      hideProgressWeb();
    });
  }, [location.state.query]);

  const onNextClick = useCallback(() => {
    if (location.state.apply.isReissue) { // 카드 재발급으로 들어온 경우 핀코드 화면으로 바로 이동
      navigate('/apply/cardDesignSelect', {
        state: { ...location.state }
      });
    } else { // 재발급이 아닌 경우 동의화면으로 이동
      navigate('/apply/step1', {
        state: { ...location.state }
      });
    }
  }, [location.state, navigate]);

  // 뒤로가기 처리 (backKeyEvent_App (APP > WEB))
  const callbackBackKeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    // 내외국인 구분
    setForeignerDivision(location.state.apply.foreignerDivisionCode === '1'? 'L': 'F')
    // 신분증 업로드 필요 여부 판단
    checkApprovalDate();
  }, [location.state.apply.foreignerDivisionCode, checkApprovalDate]);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackKeyEvent);
    return () => document.removeEventListener("backpress", callbackBackKeyEvent);
  }, [callbackBackKeyEvent]);

  useEffect(() => {
    // 언어 설정
    i18n.changeLanguage(location.state.query.languageCode);
    // 타이틀 설정
    callNativeWeb(screenId, t(screenId + ".title", "CardApplication Requirements"));
    // 하단 메뉴 숨김처리
    footerShowEventWeb("N");

    // eslint-disable-next-line
  }, []);

  return(
    <div id="wrap">
      <div id="container" className="cardRequirementInfo pcHidden">
        <section className="contents">
          <p className={"title"}>{t(screenId + '.explan01', 'Please prepare below details for card application.')}</p>
          <div className={"roundedRectangleWrap"}>
            <div className={"content"}>
              <div>
                <p className={"header"}>{t(screenId + '.requirementHeader01', 'Card Delivery Address')}</p>
                <p className={"description"}>{t(screenId + '.requirementExplan01', 'You can upload the address photo instead of entering the address')}</p>
              </div>
              <div className={"imgWrap halfWidth"}>
                <img src={'/assets/images/content/card_delivery_1.png'} alt={'Address 1'}/>
                <img className={"mgl24"} src={'/assets/images/content/card_delivery_2.png'} alt={'Address 2'}/>
              </div>
            </div>
            {isNeedIdUpload ?
              <div>
                <div className={"divider"}/>
                <div className={'content'}>
                  <p className={"header"}>{t(screenId + '.requirementHeader02', 'Your ID Document')}</p>
                  {foreignerDivision === 'L' ? (
                    <div className={"mgt8"}>
                      <p>{t(screenId + '.requirementExplan02.02', 'Korean Residence Card')}</p>
                      <div className={"imgWrap"} style={{
                        justifyContent: 'center'
                      }}>
                        <img src={'/assets/images/content/id_card_korean.png'} alt={'Korean Resident Registration Card'}
                             style={{ width: '132px' }}/>
                      </div>
                    </div>
                  ) : (
                    <div className={'mgt8'}>
                      <p>{t(screenId + '.requirementExplan02.01', 'Alien Registration Card or Passport')}</p>
                      <div className={'imgWrap'} style={{justifyContent: 'center'}}>
                        <img style={{width: '132px'}} src={'/assets/images/content/id_card.png'} alt={'ID card'}/>
                        <img className={"mgl24"} style={{width: '118px'}} src={'/assets/images/content/passport.png'} alt={'Passport'}/>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              : <div/>
            }
          </div>
          <div className={'btnArea'}>
            <button className={'btnPrimaryTy01'} onClick={onNextClick}>
              <span>Next</span>
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

export default ApplyRequirements;